import type { ComponentType } from "react";
import { BookUser, FileText, ScanSearch } from "lucide-react";
import type { DockPanel } from "../../../../stores/dock";
import type { DockKindDef, DockKindProps } from "../../registry";
import { ContactInspectorBody } from "./ContactInspectorBody";
import { ProposalInspectorBody } from "./ProposalInspectorBody";
import { inspectorData } from "./data";
import { TaskInspector } from "../../../../pages/tasks/TaskInspector";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
const ProjectWorkspace = lazy(() => import("../../../../pages/projects/ProjectsPage").then(module => ({ default: module.ProjectsPage })));
import { useDockStore } from "../../../../stores/dock";

export function ProjectInspector({ panel }: { panel: DockPanel }) {
  const { t } = useTranslation();
  return <Suspense fallback={<p role="status">{t("projects.loading", "Loading project…")}</p>}><ProjectWorkspace key={panel.id} inspectorSlug={String(inspectorData(panel).props.slug)} onClose={() => useDockStore.getState().closePanel(panel.id)} /></Suspense>;
}

export { contactInspectorPanel, inspectorData, type InspectorData } from "./data";

/**
 * The inspector kind (2026-08-28, owner directive) — item detail/edit
 * panels live in the DOCK beside terminal and artifacts, not as page-local
 * asides. One kind, many inspectors: the Body dispatches on
 * `data.inspector`, so a future merchant/listing/entity inspector is one
 * entry in this map plus a panel builder in data.ts — the dock host stays
 * untouched (the registry's founding contract).
 */
const INSPECTORS: Record<string, ComponentType<{ panel: DockPanel }>> = {
	contact: ContactInspectorBody,
	proposal: ProposalInspectorBody,
  task: TaskInspector,
  project: ProjectInspector,
};

function InspectorBody({ panel }: DockKindProps) {
	const { inspector } = inspectorData(panel);
	const Body = INSPECTORS[inspector];
	if (!Body) {
		// An unknown inspector is a real state (a panel from a newer build, a
		// typo in a builder) — say so, never draw an indistinguishable blank.
		return (
			<div style={{ padding: 16, fontSize: 13, color: "var(--text-muted)" }}>
				No inspector registered for “{inspector || "(empty)"}”.
			</div>
		);
	}
	return <Body panel={panel} />;
}

function tabIconFor(panel: DockPanel) {
	const { inspector } = inspectorData(panel);
	if (inspector === "contact") return <BookUser size={13} />;
	if (inspector === "proposal") return <FileText size={13} />;
	return <ScanSearch size={13} />;
}

export const inspectorKind: DockKindDef = {
	tabIcon: tabIconFor,
	Body: InspectorBody,
	// No `create`: an inspector is opened by clicking an item in its module —
	// it cannot be conjured from nothing, exactly like an artifact.
};
